// ─── i18n bootstrap (Phase 9 Batch B, ADR pending) ───
// Owner decisions (docs/phase9/batch-b-i18n.md §9):
// - Spanish is the hard default: new devices and new users are 'es'. We
//   deliberately do NOT read navigator.language.
// - The pre-login screen bootstraps from the `a8planner_lang` localStorage mirror
//   (last signed-in user's choice); after login, `user.language` from
//   /auth/me is authoritative and re-syncs the mirror (see applyUserLanguage).
// - UI chrome only. User content is never translated. APP_NAME is a proper
//   noun passed as an interpolation var, never a translated string.

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const SUPPORTED_LANGUAGES = ['es', 'en'] as const;
export type Language = (typeof SUPPORTED_LANGUAGES)[number];

export const LANG_STORAGE_KEY = 'a8planner_lang';
export const DEFAULT_LANGUAGE: Language = 'es';

// Namespaces are auto-registered from the locale folders via Vite's glob —
// dropping a new `<ns>.json` into locales/<lang>/ wires it up with ZERO edits
// here (the extraction PRs, B3–B6, only touch their own namespace files +
// feature dirs, never this bootstrap). File name = namespace name.
// Strict per-key typing lives in i18next.d.ts, which imports the `en` files
// (English is the source-of-truth language).
// ─── Only the language in use is downloaded — GHE #1029 ───
//
// 🚨 Both catalogues used to be `eager: true`, which inlined them into the entry chunk: measured
// at 434 KB of its 528 KB raw — 82% of the app bundle, and ~105 KB gzipped of a 265 KB initial
// budget that was sitting at 100%. Every user downloaded both languages and all 26 namespaces
// before the first paint, to read one of them.
//
// Spanish stays EAGER because it is the hard default (see the header) and the majority: keeping
// it inline is what lets this whole module stay synchronous, so app bootstrap is untouched and
// the common path costs nothing. English is a separate chunk, fetched only if somebody wants it.
//
// ⚠️ THE COST, accepted by the owner 2026-09-03: an English user briefly sees Spanish on first
// load, until the chunk lands. Option B was awaiting the right language before first render —
// correct for everyone, but it makes bootstrap async for the benefit of the minority.
//
// 🚨 WHAT MAKES DROPPING THE OTHER LANGUAGE SAFE: `fallbackLng` is 'en', so a Spanish key that
// went missing would silently degrade to a raw key string. It cannot — `localeKeyParity.test.ts`
// enforces es↔en key parity across every namespace. That gate is load-bearing for this file;
// weakening it re-opens a silent failure, not a cosmetic one.
const eagerModules = import.meta.glob<{ default: Record<string, unknown> }>('./locales/es/*.json', {
  eager: true,
});

/** English, one chunk per namespace, fetched on demand. */
const lazyModules = import.meta.glob<{ default: Record<string, unknown> }>('./locales/en/*.json');

/** `./locales/<lang>/<ns>.json` → `<ns>`, or null when the path is not one. */
function namespaceOf(path: string): string | null {
  return /\/locales\/[^/]+\/([^/]+)\.json$/.exec(path)?.[1] ?? null;
}

function buildResources(): Record<string, Record<string, Record<string, unknown>>> {
  const es: Record<string, Record<string, unknown>> = {};
  for (const [path, mod] of Object.entries(eagerModules)) {
    const ns = namespaceOf(path);
    if (ns !== null) es[ns] = mod.default;
  }
  return { es };
}

export const resources = buildResources();

/**
 * Which languages were bundled EAGERLY — captured here, before anything can change it.
 *
 * 🚨 Not derivable from `resources` afterwards: i18next stores the object it is given and
 * `addResourceBundle` writes straight into it, so once English has been fetched `resources.en`
 * exists and the object no longer describes what shipped. A test reading it would pass for
 * entirely the wrong reason. This snapshot is the honest answer to "what is in the entry chunk".
 */
export const EAGER_LANGUAGES: readonly string[] = Object.freeze(Object.keys(resources));

/** Languages already added to i18next, so a second request is a no-op rather than a refetch. */
const loaded = new Set<Language>(['es']);

/** Is this language's catalogue already in memory? Lets callers stay synchronous when it is. */
export function isLanguageLoaded(language: Language): boolean {
  return loaded.has(language);
}

/**
 * Make sure a language's namespaces are available, fetching them if they are not.
 *
 * ⚠️ Resolves immediately for Spanish and for anything already loaded. Callers may await it
 * unconditionally.
 *
 * ⚠️ Failures are swallowed on purpose. A chunk that will not load leaves the UI in Spanish,
 * which is the app's own default — noisier handling would replace a legible screen with an
 * error for a preference.
 */
export async function ensureLanguageLoaded(language: Language): Promise<void> {
  if (loaded.has(language)) return;
  const entries = Object.entries(lazyModules);
  await Promise.all(
    entries.map(async ([path, load]) => {
      const ns = namespaceOf(path);
      if (ns === null) return;
      try {
        const mod = await load();
        i18n.addResourceBundle(language, ns, mod.default, true, true);
      } catch {
        // See the note above: staying in Spanish beats failing loudly over a language file.
      }
    }),
  );
  loaded.add(language);
}

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

/** Pre-login language: the localStorage mirror, else hard Spanish. */
export function detectInitialLanguage(): Language {
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (isLanguage(stored)) return stored;
  } catch {
    // storage unavailable (private mode edge) — fall through to default
  }
  return DEFAULT_LANGUAGE;
}

const initialLanguage = detectInitialLanguage();

void i18n.use(initReactI18next).init({
  resources,
  // 🚨 Initialised at the language we HAVE, not the one we want — GHE #1029. Starting at 'en'
  // before its chunk lands would render raw keys, which is worse than the wrong language. The
  // switch happens below, once the strings exist.
  lng: initialLanguage === 'es' ? 'es' : DEFAULT_LANGUAGE,
  fallbackLng: 'en', // English is the developer-authored source language
  defaultNS: 'common',
  interpolation: {
    // React already escapes; double-escaping would mangle e.g. apostrophes.
    escapeValue: false,
  },
  returnNull: false,
});

// Keep <html lang> in sync for a11y / hyphenation.
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});
document.documentElement.lang = i18n.language;

// The pre-login mirror asked for English: fetch it and switch as soon as it is here. Deliberately
// NOT awaited — bootstrap stays synchronous, which is the whole point of keeping Spanish eager.
if (initialLanguage !== DEFAULT_LANGUAGE) {
  void ensureLanguageLoaded(initialLanguage).then(() => i18n.changeLanguage(initialLanguage));
}

/**
 * Apply the server-stored preference (authoritative after login) and keep the
 * pre-login mirror in sync so the NEXT visit's login screen already matches.
 */
export function applyUserLanguage(language: string): void {
  if (!isLanguage(language)) return;
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, language);
  } catch {
    // mirror is best-effort only
  }
  if (i18n.language !== language) {
    // 🚨 Loaded BEFORE switching. Changing first would show raw keys for as long as the chunk
    // takes to arrive — the one failure mode worse than the wrong language.
    //
    // ⚠️ Synchronous when the catalogue is already in memory, which is always true for Spanish.
    // Deferring that case to a microtask for no reason would make a synchronous API asynchronous
    // for the majority path, and it is the kind of change that shows up as a flicker rather than
    // as a failure.
    if (isLanguageLoaded(language)) {
      void i18n.changeLanguage(language);
    } else {
      void ensureLanguageLoaded(language).then(() => i18n.changeLanguage(language));
    }
  }
}

export default i18n;
